// docs/86 the World's storybook on the client: `book_chapter` rows (one per chapter of four main
// quests) and the scene that opened each chapter after the first, decoded by the wasm
// (`decode_chapter_scene`, serde snake_case; the module blanks the beats). Each scene is played
// once per browser, like the intro; the marks live beside the intro's.
import { wasmModule } from '../sim/wasm';
import type { Line, Panel } from './genesis';

/** The named foe's moment as the chapter turns. */
export interface FoeTurn { npc: number | bigint; name: string; title: string; text: string; line: string }
export interface ChapterScene {
  closed_title: string;
  closed_summary: string;
  title: string;
  goal: string;
  panels: Panel[];
  foe: FoeTurn | null;
}

/** `book_chapter.state` (fk-world book.rs). */
export const CHAPTER = { OPEN: 0, CLOSING: 1, CLOSED: 2 } as const;

const WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty'];
/** "Two" (fk-schema `number_word`). */
export const numberWord = (n: number) => WORDS[n] ?? String(n);
/** "Chapter Two" / "Chapter Two · The Salt Road". */
export const chapterLabel = (n: number, title?: string) => `Chapter ${numberWord(n)}${title?.trim() ? ` · ${title.trim()}` : ''}`;
/** Main quests in a chapter (fk-schema `QUESTS_PER_CHAPTER`). */
export const QUESTS_PER_CHAPTER = 4;

const cache = new Map<string, ChapterScene | null>();
/** Decode a chapter's scene once (keyed by its bytes' length and a cheap hash). */
export function decodeChapterScene(bytes: Uint8Array | undefined): ChapterScene | undefined {
  if (!bytes || bytes.length === 0) return undefined;
  let h = 2166136261;
  for (const b of bytes) { h ^= b; h = Math.imul(h, 16777619); }
  const key = `${bytes.length}:${h >>> 0}`;
  if (!cache.has(key)) {
    try { cache.set(key, wasmModule().decode_chapter_scene(bytes) as ChapterScene); } catch (err) { console.error('[story] decode_chapter_scene failed', err); cache.set(key, null); }
  }
  return cache.get(key) ?? undefined;
}

/** A scene for the `?chapter=1` harness (no server): the shape the storyteller writes. */
export function sampleChapterScene(foe = true): ChapterScene {
  const line = (speaker: string, text: string): Line => ({ speaker, text });
  return {
    closed_title: 'The Drowned Bell',
    closed_summary: 'The heroes raised the drowned bell from the mere, lost the ferry to the salt-thieves, and drove Wartlip back into the reeds.',
    title: 'The Salt Road',
    goal: 'Find where the salt-thieves are carrying the stolen salt before the mere freezes over.',
    panels: [
      { scene: 'Dawn', heading: 'The bell rings again', text: 'For the first time in a generation the bell rings over Hollowmere, and {heroes} hear it from the causeway. Reeve Odile is waiting at the top of the steps, a lantern still lit in her hand though the sun is up.', line: line('Reeve Odile', 'You brought it up. Whatever else happens this winter, you did that.') },
      { scene: 'Marsh', heading: 'What the ferry cost', text: 'Below the steps the ferry lies on its side in the shallows, burned to the waterline. Without it the far villages are cut off, and the salt that keeps them through the cold went with the thieves.', line: line('Reeve Odile', 'Three villages will go hungry by midwinter unless we get that salt back.') },
      { scene: 'Road', heading: 'Tracks to the north', text: 'Odile kneels where the cart tracks leave the mud and turn north onto the old salt road. They are deep: the carts were heavy, and they were in no hurry.', line: line('Reeve Odile', 'They want us to follow. So we follow, but carefully.') },
    ],
    foe: foe ? { npc: 1100, name: 'Wartlip', title: 'the Dam-Breaker', text: 'Far up the salt road, in a barn that stinks of brine, Wartlip counts the stolen sacks by torchlight and listens to the bell ringing across the water.', line: 'Let them ring their bell. Bells do not keep anyone warm.' } : null,
  };
}
